import React, { useEffect, useRef, useState } from 'react';
import { Smartphone, Share, PlusSquare, Download, CheckCircle2, BellRing, Info, MoreVertical, BellOff, Loader2, RefreshCw } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useInstallPrompt, isIos, forceAppUpdate } from '../../lib/pwa';
import { getStoredUser } from '../../lib/easerApi';
import { pushSupported, currentSubscription, enablePush, disablePush } from '../../lib/push';
import { HomeLayoutCard } from './HomeLayoutCard';

/** GRH > Paramètres: install the Easer portal as an app on the home screen, phone notifications, home layout. */
export function EaserSettings({ onGoHome }) {
  const { canPrompt, install, installed } = useInstallPrompt();
  const canPromptRef = useRef(canPrompt);
  useEffect(() => { canPromptRef.current = canPrompt; }, [canPrompt]);
  const [perm, setPerm] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'));
  const [pushOn, setPushOn] = useState(null); // null = checking, true/false = this device subscribed to agency pushes
  const [pushBusy, setPushBusy] = useState(false);
  const user = getStoredUser();

  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    const t = setInterval(() => setPerm(Notification.permission), 2000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!pushSupported()) { setPushOn(false); return; }
    currentSubscription().then((sub) => setPushOn(!!sub && Notification.permission === 'granted')).catch(() => setPushOn(false));
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.info('Notifications push désactivées sur ce téléphone');
      } else {
        const r = await enablePush();
        setPerm(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
        if (r.ok) { setPushOn(true); toast.success('Notifications push activées : vous recevrez les messages de l\'agence même application fermée'); }
        else if (r.reason === 'denied') toast.warning('Notifications refusées : réactivez-les dans les réglages du navigateur');
        else if (r.reason === 'unsupported') toast.warning('Ce navigateur ne prend pas en charge les notifications push');
        else toast.error('Activation impossible pour le moment');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Activation impossible pour le moment');
    } finally {
      setPushBusy(false);
    }
  };

  const [installing, setInstalling] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [lastCheck, setLastCheck] = useState(() => localStorage.getItem('pts_easer_last_update_check'));

  // Fetch the latest version, clear caches and reload: fixes a phone stuck on old code after a release.
  const onUpdate = async () => {
    setUpdating(true);
    toast.info('Recherche de la dernière version…');
    try {
      const { updated } = await forceAppUpdate();
      const stamp = new Date().toISOString();
      localStorage.setItem('pts_easer_last_update_check', stamp);
      setLastCheck(stamp);
      toast.success(updated ? 'Nouvelle version installée, rechargement…' : 'Application à jour, rechargement…');
    } finally {
      setTimeout(() => window.location.reload(), 600);
    }
  };

  // One click: use the browser prompt when available; if it has not fired yet, wait a moment for it, then fall back
  // to the manual steps (browser without install prompt).
  const onInstall = async () => {
    setInstalling(true);
    setShowManual(false);
    try {
      let ready = canPrompt;
      for (let i = 0; i < 6 && !ready; i += 1) {
        await new Promise((r) => setTimeout(r, 500)); // eslint-disable-line no-await-in-loop
        ready = canPromptRef.current;
      }
      const ok = await install();
      if (ok) toast.success('Espace Easer ajouté à votre écran d\'accueil');
      else if (!ready) setShowManual(true);
    } finally {
      setInstalling(false);
    }
  };

  const askNotifications = async () => {
    try {
      const r = await Notification.requestPermission();
      setPerm(r);
      if (r === 'granted') toast.success('Notifications du téléphone activées');
      else if (r === 'denied') toast.warning('Notifications refusées : réactivez-les dans les réglages du navigateur');
    } catch { /* unsupported */ }
  };

  return (
    <div className="space-y-4" data-testid="easer-settings">
      <Card className="p-4 border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5" data-testid="settings-install-card">
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center flex-shrink-0"><Smartphone className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-base">Espace Easer en application sur l'écran d'accueil</p>
            {installed ? (
              <p className="text-sm mt-1 flex items-center gap-1.5 text-[hsl(var(--primary))] font-medium" data-testid="settings-install-done"><CheckCircle2 className="h-4 w-4" /> L'application est installée : vous l'utilisez en ce moment.</p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mt-0.5">Ouverture en un geste, plein écran, pointage possible même sans réseau.</p>
                {isIos() ? (
                  <div className="mt-2 text-sm leading-relaxed rounded-lg bg-card border border-border p-3" data-testid="settings-install-ios">
                    <p className="font-medium mb-1">Sur iPhone / iPad, Apple n'autorise pas l'installation en un clic : deux gestes suffisent.</p>
                    <ol className="list-decimal pl-5 space-y-0.5">
                      <li>Dans Safari, touchez <Share className="inline h-4 w-4 align-text-bottom" /> <strong>Partager</strong> (en bas de l'écran).</li>
                      <li>Choisissez <PlusSquare className="inline h-4 w-4 align-text-bottom" /> <strong>« Sur l'écran d'accueil »</strong> puis <strong>Ajouter</strong>.</li>
                    </ol>
                  </div>
                ) : (
                  <>
                    <Button className="mt-3 h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" onClick={onInstall} disabled={installing} data-testid="settings-install-button">
                      {installing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Installer l'application
                    </Button>
                    {showManual && (
                      <p className="text-sm mt-2 leading-relaxed rounded-lg bg-card border border-border p-3" data-testid="settings-install-manual">
                        Ce navigateur ne propose pas l'installation automatique ici. Ouvrez le menu du navigateur <MoreVertical className="inline h-4 w-4 align-text-bottom" /> et choisissez <strong>« Installer l'application »</strong> ou <strong>« Ajouter à l'écran d'accueil »</strong>. Sur Android, Chrome est recommandé.
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-4" data-testid="settings-update-card">
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><RefreshCw className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Mise à jour de l'application</p>
            <p className="text-sm text-muted-foreground mt-0.5">Un bloc qui ne s'affiche plus, une nouveauté absente ? Ce bouton récupère la dernière version, vide le cache du téléphone et relance l'application. Vos pointages en attente sont conservés.</p>
            <Button variant="outline" className="mt-3 h-11" onClick={onUpdate} disabled={updating} data-testid="settings-update-button">
              {updating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Mettre à jour l'application
            </Button>
            {lastCheck && <p className="text-xs text-muted-foreground mt-2" data-testid="settings-update-last-check">Dernière mise à jour : {new Date(lastCheck).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
          </div>
        </div>
      </Card>

      <HomeLayoutCard onPreview={onGoHome} />

      <Card className="p-4" data-testid="settings-push-card">
        <div className="flex items-start gap-3">
          <span className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${pushOn ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'}`}>{pushOn ? <BellRing className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}</span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Messages de l'agence sur ce téléphone</p>
            <p className="text-sm text-muted-foreground mt-0.5">Challenges et messages de l'agence arrivent en notification, même quand l'application est fermée.{isIos() && !installed ? ' Sur iPhone, installez d\'abord l\'application sur l\'écran d\'accueil.' : ''}</p>
            {pushSupported() ? (
              <Button variant={pushOn ? 'outline' : 'default'} className={`mt-3 h-11 ${pushOn ? '' : 'bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white'}`} onClick={togglePush} disabled={pushBusy || pushOn === null} data-testid="settings-push-toggle" aria-pressed={!!pushOn}>
                {pushBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : pushOn ? <BellOff className="h-4 w-4 mr-1" /> : <BellRing className="h-4 w-4 mr-1" />}
                {pushOn ? 'Désactiver sur ce téléphone' : 'Activer les notifications push'}
              </Button>
            ) : <p className="text-sm mt-2 text-muted-foreground" data-testid="settings-push-unsupported">Non pris en charge par ce navigateur.</p>}
            {pushOn && <p className="text-sm mt-2 flex items-center gap-1.5 text-[hsl(var(--primary))] font-medium" data-testid="settings-push-on"><CheckCircle2 className="h-4 w-4" /> Activées sur ce téléphone</p>}
          </div>
        </div>
      </Card>

      <Card className="p-4" data-testid="settings-notifications-card">
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center flex-shrink-0"><BellRing className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Rappels pendant la journée</p>
            <p className="text-sm text-muted-foreground mt-0.5">Fin de prestation oubliée, pause de plus de 30 min : une notification vous le rappelle quand l'application est ouverte.</p>
            {perm === 'granted' && <p className="text-sm mt-2 flex items-center gap-1.5 text-[hsl(var(--primary))] font-medium" data-testid="settings-notif-granted"><CheckCircle2 className="h-4 w-4" /> Activés</p>}
            {perm === 'default' && <Button variant="outline" className="mt-3 h-11" onClick={askNotifications} data-testid="settings-notif-ask"><BellRing className="h-4 w-4 mr-1" /> Activer les rappels</Button>}
            {perm === 'denied' && <p className="text-sm mt-2 text-muted-foreground" data-testid="settings-notif-denied">Refusés pour ce site : réactivez-les dans les réglages du navigateur (Notifications).</p>}
            {perm === 'unsupported' && <p className="text-sm mt-2 text-muted-foreground">Non pris en charge par ce navigateur.</p>}
          </div>
        </div>
      </Card>

      <Card className="p-4" data-testid="settings-account-card">
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-secondary text-foreground flex items-center justify-center flex-shrink-0"><Info className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-semibold">Mon compte</p>
            <p className="text-muted-foreground mt-0.5">{user?.name || user?.firstname}{user?.login ? ` · identifiant Dolibarr ${user.login}` : ''}</p>
            <p className="text-muted-foreground mt-1">Mot de passe oublié ou à changer : il se gère dans Dolibarr, contactez l'agence au 03 74 47 47 82.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
